import './style.scss';

import { SelectExchangeField } from 'fields/SelectExchangeField';
import { SelectNetworkField } from 'fields/SelectNetworkField';
import isNil from 'lodash/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IDex } from 'api/apiDictionary/models';
import { useTypedDispatch, useTypedSelector } from 'store';
import { getDexs } from 'store/slices/dictionary';
import { Button } from 'ui/Button/Button';
import { Modal } from 'ui/Modal/Modal';
import { ENetwork, web3Configs } from 'web3';

interface ISelectPairDexModalProps {
  dex: IDex | undefined;
  setDex: (dex: IDex | undefined) => void;
  openSelectedDexPairWindow: () => void;
  onClose: () => void;
}

const getDexVenueKey = (dex: IDex['dex']): string => dex.split(':')[0];

export const SelectPairDexModal = ({
  dex,
  setDex,
  openSelectedDexPairWindow,
  onClose,
}: ISelectPairDexModalProps) => {
  const dispatch = useTypedDispatch();
  const dexs = useTypedSelector(store => store.dictionary.dexs);

  const [selectedDexVenue, setSelectedDexVenue] = useState<string | undefined>(
    dex ? getDexVenueKey(dex.dex) : undefined,
  );
  const [selectedNetwork, setSelectedNetwork] = useState<ENetwork | undefined>(dex?.network);

  useEffect(() => {
    if (!dexs) {
      dispatch(getDexs());
    }
  }, [dexs, dispatch]);

  const handleNext = useCallback(() => {
    openSelectedDexPairWindow();
  }, [openSelectedDexPairWindow]);

  const dexItems = useMemo(() => {
    const venues = new Map<string, { exchange: IDex['dex']; label: string }>();

    dexs?.forEach(item => {
      const venueKey = getDexVenueKey(item.dex);

      if (!venues.has(venueKey)) {
        venues.set(venueKey, { label: item.description, exchange: item.dex });
      }
    });

    return [...venues.values()];
  }, [dexs]);

  const networkItems = useMemo(() => {
    if (!selectedDexVenue || !dexs) return [];

    return dexs
      .filter(item => getDexVenueKey(item.dex) === selectedDexVenue)
      .map(item => ({ label: web3Configs[item.network].label, network: item.network }));
  }, [dexs, selectedDexVenue]);

  const isJupiter = dex?.dex === 'jupiter:solana';

  return (
    <Modal
      title={'Select DEX'}
      onClose={onClose}
      className={'mm-select-dex-pair-modal'}
      customButtons={
        !isJupiter ? (
          <Button onClick={handleNext} disabled={isNil(dex)}>
            Next
          </Button>
        ) : undefined
      }
    >
      <div className="mm-select-dex-pair-modal__container">
        <SelectExchangeField
          items={dexItems}
          onSelectItem={item => {
            setSelectedDexVenue(item ? getDexVenueKey(item.exchange as IDex['dex']) : undefined);
            setSelectedNetwork(undefined);
            setDex(undefined);
          }}
          label="Select DEX"
          selectedItem={dexItems.find(item => getDexVenueKey(item.exchange) === selectedDexVenue)}
        />
        <SelectNetworkField
          items={networkItems}
          onSelectItem={item => {
            const network = item?.network;
            setSelectedNetwork(network);
            setDex(
              network
                ? dexs?.find(
                    candidate =>
                      getDexVenueKey(candidate.dex) === selectedDexVenue &&
                      candidate.network === network,
                  )
                : undefined,
            );
          }}
          label="Select chain"
          selectedItem={networkItems.find(item => item.network === selectedNetwork)}
          disabled={!selectedDexVenue}
        />
        {isJupiter && (
          <p className="text-sm text-secondary" role="status">
            Jupiter is a route aggregator and has no single pool address. Select a pool-based DEX,
            such as Raydium AMM V4, to add a pair by pool address.
          </p>
        )}
      </div>
    </Modal>
  );
};
